import React, { useRef, useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { API_BASE } from "../api";

function LivePortrait() {
  useEffect(() => {
    document.title = "Live Portrait";
  }, []);

  // Session state (repurposed; webcam/recording logic removed)
  const [sessionActive, setSessionActive] = useState(false);
  const [status, setStatus] = useState("");
  const [timer, setTimer] = useState(0);

  // Participant ID input state (used for session)
  const [deelnemerId, setDeelnemerId] = useState("");
  // validate deelnemer id: only digits, exactly 3 chars
  const isValidDeelnemerId = /^\d{3}$/.test(deelnemerId);
  const handleDeelnemerChange = (e) => {
    const digits = String(e.target.value || "").replace(/\D/g, "").slice(0, 3);
    setDeelnemerId(digits);
  };

  // Participant lookup state: null = not checked / input invalid, true = found, false = not found
  const [participantName, setParticipantName] = useState(null);
  const [participantFound, setParticipantFound] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const lookup = async (id) => {
      try {
        const res = await fetch(`${API_BASE}/deelnemers`);
        if (!res.ok) throw new Error("Network response was not ok");
        const data = await res.json();
        if (cancelled) return;
        const found = data.find((p) => String(p.id) === id || String(p.id).padStart(3, "0") === id);
        if (found) {
          setParticipantName(found.naam || found.name || `#${found.id}`);
          setParticipantFound(true);
        } else {
          setParticipantName(null);
          setParticipantFound(false);
        }
      } catch (err) {
        if (!cancelled) {
          setParticipantName(null);
          setParticipantFound(false);
        }
      }
    };

    if (isValidDeelnemerId) {
      setParticipantFound(null); // pending
      lookup(deelnemerId);
    } else {
      setParticipantName(null);
      setParticipantFound(null);
    }

    return () => {
      cancelled = true;
    };
  }, [deelnemerId, isValidDeelnemerId]);

  // UI phases: idle (form), countdown, analyze, video, finished
  const [phase, setPhase] = useState("idle");
  const [countdown, setCountdown] = useState(10);
  const [analyzePct, setAnalyzePct] = useState(0);

  // Refs for intervals/timeouts
  const countdownIntervalRef = useRef(null);
  const analyzeIntervalRef = useRef(null);
  const analyzeStartRef = useRef(null);
  const globalTimeoutRef = useRef(null); // 60s from start -> finished
  const finishedResetTimeoutRef = useRef(null);
  const timerIntervalRef = useRef(null);

  // Build dynamic video src with cache-busting retry key (kept for compatibility)
  const retryKeyRef = useRef(0);
  const videoSrc = `${API_BASE}/liveportraits/lp--${deelnemerId}.mp4${retryKeyRef.current ? `?x=${retryKeyRef.current}` : ""}`;

  // Utility clear helpers
  const clearIntervalIfExists = (ref) => {
    try {
      if (ref && ref.current) {
        clearInterval(ref.current);
        ref.current = null;
      }
    } catch (e) {}
  };

  const clearTimeoutIfExists = (ref) => {
    try {
      if (ref && ref.current) {
        clearTimeout(ref.current);
        ref.current = null;
      }
    } catch (e) {}
  };

  // Reset session to initial idle state
  const resetToIdle = () => {
    setPhase("idle");
    setStatus("");
    setTimer(0);
    setDeelnemerId("");
    setAnalyzePct(0);
    setCountdown(10);
    setSessionActive(false);
    setVideoError(false);
    setParticipantName(null);
    setParticipantFound(null);

    clearIntervalIfExists(countdownIntervalRef);
    clearIntervalIfExists(analyzeIntervalRef);
    clearIntervalIfExists(timerIntervalRef);
    clearTimeoutIfExists(globalTimeoutRef);
    clearTimeoutIfExists(finishedResetTimeoutRef);
  };

  // Analyze phase: 7 seconds to reach 100%
  const startAnalyzePhase = () => {
    setPhase("analyze");
    setAnalyzePct(0);
    analyzeStartRef.current = Date.now();

    clearIntervalIfExists(analyzeIntervalRef);
    analyzeIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - analyzeStartRef.current;
      const pct = Math.min(100, Math.floor((elapsed / 7000) * 100));
      setAnalyzePct(pct);
      if (pct >= 100) {
        clearIntervalIfExists(analyzeIntervalRef);
        setPhase("video");
      }
    }, 100);
  };

  // Start the session flow when the user presses START
  const handleStartClick = () => {
    if (!participantFound || sessionActive) return;

    setSessionActive(true);
    setStatus("");
    setTimer(0);

    // Start 60s global timeout that will show the finished message
    clearTimeoutIfExists(globalTimeoutRef);
    globalTimeoutRef.current = setTimeout(() => {
      // Force finish UI regardless of current phase
      clearIntervalIfExists(countdownIntervalRef);
      clearIntervalIfExists(analyzeIntervalRef);

      setPhase("finished");

      // After 5 seconds, reset to idle and clear deelnemerId
      clearTimeoutIfExists(finishedResetTimeoutRef);
      finishedResetTimeoutRef.current = setTimeout(() => {
        resetToIdle();
      }, 5000);
    }, 60000);

    // Start a simple timer for UI purposes (optional)
    clearIntervalIfExists(timerIntervalRef);
    timerIntervalRef.current = setInterval(() => {
      setTimer((t) => t + 1);
    }, 1000);

    // Kick off countdown
    setPhase("countdown");
    setCountdown(10);
    clearIntervalIfExists(countdownIntervalRef);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearIntervalIfExists(countdownIntervalRef);
          startAnalyzePhase();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  // Video error state (kept minimal for compatibility with previous UI)
  const [videoError, setVideoError] = useState(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearIntervalIfExists(countdownIntervalRef);
      clearIntervalIfExists(analyzeIntervalRef);
      clearIntervalIfExists(timerIntervalRef);
      clearTimeoutIfExists(globalTimeoutRef);
      clearTimeoutIfExists(finishedResetTimeoutRef);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: "1rem" }}>
      {phase === "idle" && !sessionActive && (
        <h1 style={{ fontSize: "3em", textAlign: "center", margin: 0 }}>Live Portrait</h1>
      )}

      {/* ID input and START button shown only in idle phase */}
      {phase === "idle" && !sessionActive && (
        <>
          <input
            id="deelnemerId"
            type="text"
            value={deelnemerId}
            onChange={handleDeelnemerChange}
            placeholder="000"
            maxLength={3}
            inputMode="numeric"
            pattern="\\d{3}"
            style={{ width: "30%", fontSize: "5em", textAlign: "center", padding: "0.2em", borderRadius: "0.6em" }}
          />
          {/* Participant name or not-found message */}
          <div style={{ fontSize: "1.5em", marginTop: "0.2em" }}>
            {participantFound === null ? "" : participantFound === true ? participantName : "Participant not found"}
          </div>
          <Button
            variant="primary"
            className="start-button"
            onClick={handleStartClick}
            disabled={sessionActive || !participantFound}
            style={{ width: "30%", fontSize: "5em", marginTop: "0.5em", borderRadius: "0.6em" }}
          >
            START
          </Button>
        </>
      )}

      {/* Countdown phase */}
      {phase === "countdown" && (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "2em", marginBottom: "0.5em" }}>Capturing participant's brain waves</div>
          <div style={{ fontSize: "6em", fontWeight: "700" }}>{countdown}</div>
        </div>
      )}

      {/* Analyze phase */}
      {phase === "analyze" && (
        <div style={{ textAlign: "center", width: "80%" }}>
          <div style={{ fontSize: "2em", marginBottom: "1em" }}>Analyzing</div>
          <div style={{ width: "100%", height: "2em", background: "#333", borderRadius: "1em", overflow: "hidden" }}>
            <div style={{ width: `${analyzePct}%`, height: "100%", background: "#0b84ff", transition: "width 100ms linear", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600 }}>
              {analyzePct}%
            </div>
          </div>
        </div>
      )}

      {/* Video phase */}
      {phase === "video" && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100vh" }}>
          <div style={{ position: "relative", width: "100%", maxHeight: "92vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {videoError && (
              <div style={{ position: "absolute", zIndex: 2, background: "rgba(0,0,0,0.6)", padding: "1em 1.5em", borderRadius: "0.6em", textAlign: "center" }}>
                <div>Looking for brain activity...<br/>No results.<br/>Retrying in 1 second(s).</div>
              </div>
            )}
            <video
              className="fullscreen-video"
              src={videoSrc}
              autoPlay
              playsInline
              loop
              style={{ maxHeight: "92vh", width: "auto" }}
            />
          </div>
        </div>
      )}

      {/* Finished message */}
      {phase === "finished" && (
        <div style={{ textAlign: "center", fontSize: "2em", padding: "1em" }}>
          This part of the test is finished. When the whistle blows, you may proceed to the next section.
        </div>
      )}
    </div>
  );
}

export default LivePortrait;
